import { useState, useMemo, useEffect, useRef } from "react";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Calendar, ChevronDown, ChevronRight, Trash2, ListTodo, X, Clock, Pencil } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { apiRequest, queryClient, toastTaskMutationError } from "@/lib/queryClient";
import { formatDateInput, parseLocalDate } from "@/lib/dateUtils";
import type { Task, SubTask, Category } from "@shared/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface GoalTasksTabProps {
  goalId: string;
}

const taskFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  category: z.string().default("Uncategorized"),
  priority: z.string().default("medium"),
  dueDate: z.string().optional(),
  linkType: z.enum(["none", "goal", "milestone", "journal"]).default("none"),
  goalId: z.string().optional(),
  milestoneId: z.string().optional(),
  journalEntryId: z.string().optional(),
  showOnMainTaskPage: z.boolean().default(true),
});

type TaskFormInput = z.input<typeof taskFormSchema>;
type TaskFormData = z.output<typeof taskFormSchema>;

type Goal = {
  id: string;
  title: string;
};

type Milestone = {
  id: string;
  goalId: string;
  title: string;
};

type JournalEntry = {
  id: string;
  title: string;
};

export default function GoalTasksTab({ goalId }: GoalTasksTabProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [newSubTaskTexts, setNewSubTaskTexts] = useState<Record<string, string>>({});
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [dialogSubTaskText, setDialogSubTaskText] = useState("");
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const [editLinkType, setEditLinkType] = useState<"none" | "goal" | "milestone" | "journal">("none");
  const [editSelectedGoalId, setEditSelectedGoalId] = useState<string>("");

  // Fetch tasks
  const { data: tasks = [], isLoading } = useQuery<Task[]>({
    queryKey: ["/api/tasks", goalId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (goalId) params.append("goalId", goalId);
      const response = await fetch(`/api/tasks?${params.toString()}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error('Failed to fetch tasks');
      return response.json();
    },
  });

  // Fetch categories
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  // Fetch all sub-tasks
  const { data: allSubTasks = [] } = useQuery<SubTask[]>({
    queryKey: ["/api/subtasks/all"],
  });

  // Fetch all goals for linking
  const { data: allGoals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  // Fetch milestones for the selected goal (for milestone linking)
  const { data: editMilestones = [] } = useQuery<Milestone[]>({
    queryKey: ["/api/goals", editSelectedGoalId, "milestones"],
    enabled: editLinkType === "milestone" && !!editSelectedGoalId,
  });

  // Fetch journal entries for linking
  const { data: journalEntries = [] } = useQuery<JournalEntry[]>({
    queryKey: ["/api/journal-entries"],
  });

  // Group sub-tasks by task ID
  const subTasksByTaskId = useMemo(() => {
    const grouped = new Map<string, SubTask[]>();
    allSubTasks.forEach(subTask => {
      if (!grouped.has(subTask.taskId)) {
        grouped.set(subTask.taskId, []);
      }
      grouped.get(subTask.taskId)!.push(subTask);
    });
    return grouped;
  }, [allSubTasks]);

  // Calculate sub-task progress
  const getSubTaskProgress = (taskId: string) => {
    const taskSubTasks = subTasksByTaskId.get(taskId) || [];
    if (taskSubTasks.length === 0) return null;
    
    const completed = taskSubTasks.filter(st => st.completed).length;
    const total = taskSubTasks.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return { completed, total, percentage };
  };

  const form = useForm<TaskFormInput, any, TaskFormData>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: "",
      description: "",
      category: "Uncategorized",
      priority: "medium",
      dueDate: "",
      showOnMainTaskPage: true,
    },
  });

  const editForm = useForm<TaskFormInput, any, TaskFormData>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: "",
      description: "",
      category: "Uncategorized",
      priority: "medium",
      dueDate: "",
    },
  });

  // Populate edit form when editTask changes
  useEffect(() => {
    if (editTask) {
      const linkType = editTask.goalId ? "goal" : editTask.milestoneId ? "milestone" : editTask.journalEntryId ? "journal" : "none";
      setEditLinkType(linkType);
      setEditSelectedGoalId(editTask.goalId || "");

      editForm.reset({
        title: editTask.title,
        description: editTask.description || "",
        category: editTask.category || "Uncategorized",
        priority: editTask.priority || "medium",
        dueDate: editTask.dueDate ? formatDateInput(new Date(editTask.dueDate)) : "",
        linkType: linkType,
        goalId: editTask.goalId || undefined,
        milestoneId: editTask.milestoneId || undefined,
        journalEntryId: editTask.journalEntryId || undefined,
      });
    }
  }, [editTask, editForm]);

  // Idempotency key: generated once per submission attempt and reused on
  // network-error retries so the server can deduplicate. Cleared on success
  // and whenever the create dialog is closed so a subsequent open starts fresh.
  const createTaskIdempotencyKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isCreateModalOpen) {
      createTaskIdempotencyKeyRef.current = null;
    }
  }, [isCreateModalOpen]);

  const createTaskMutation = useMutation({
    mutationFn: async (data: TaskFormData) => {
      const taskData = {
        title: data.title,
        description: data.description || null,
        category: data.category || "Uncategorized",
        priority: data.priority || "medium",
        completed: false,
        dueDate: data.dueDate ? parseLocalDate(data.dueDate).toISOString() : null,
        goalId: goalId,
        showOnMainTaskPage: data.showOnMainTaskPage,
      };

      if (!createTaskIdempotencyKeyRef.current) {
        createTaskIdempotencyKeyRef.current = crypto.randomUUID();
      }
      const response = await apiRequest("POST", "/api/tasks", taskData, {
        "Idempotency-Key": createTaskIdempotencyKeyRef.current,
      });
      return response.json();
    },
    onSuccess: () => {
      createTaskIdempotencyKeyRef.current = null;
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      form.reset();
      setIsCreateModalOpen(false);
    },
    onError: (error: Error) => {
      toastTaskMutationError("Couldn't create task", error, "The task was not saved.");
    },
  });

  const toggleTaskMutation = useMutation({
    mutationFn: async ({ id, completed }: { id: string; completed: boolean }) => {
      const response = await apiRequest("PATCH", `/api/tasks/${id}`, { completed });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: (error: Error) => {
      toastTaskMutationError("Couldn't update task", error, "Your change was not saved.");
    },
  });

  const createSubTaskMutation = useMutation({
    mutationFn: async ({ taskId, text }: { taskId: string; text: string }) => {
      const response = await apiRequest("POST", `/api/tasks/${taskId}/subtasks`, { text });
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", variables.taskId, "subtasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setNewSubTaskTexts(prev => ({ ...prev, [variables.taskId]: "" }));
    },
    onError: (error: Error) => {
      toastTaskMutationError("Couldn't add sub-task", error, "The sub-task was not saved.");
    },
  });

  const toggleSubTaskMutation = useMutation({
    mutationFn: async ({ id, completed }: { id: string; completed: boolean }) => {
      const response = await apiRequest("PATCH", `/api/subtasks/${id}`, { completed });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: (error: Error) => {
      toastTaskMutationError("Couldn't update sub-task", error, "Your change was not saved.");
    },
  });

  const deleteSubTaskMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/subtasks/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      toastTaskMutationError("Couldn't delete sub-task", error, "The sub-task was not deleted.");
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TaskFormData> }) => {
      const taskData: any = {
        title: data.title,
        description: data.description || null,
        category: data.category,
        priority: data.priority,
        dueDate: data.dueDate ? parseLocalDate(data.dueDate).toISOString() : null,
        goalId: null,
        milestoneId: null,
        journalEntryId: null,
      };

      if (editLinkType === "goal" && data.goalId) {
        taskData.goalId = data.goalId;
      } else if (editLinkType === "milestone" && data.milestoneId) {
        taskData.milestoneId = data.milestoneId;
        // Preserve goal association when linking to milestone
        if (editSelectedGoalId) {
          taskData.goalId = editSelectedGoalId;
        }
      } else if (editLinkType === "journal" && data.journalEntryId) {
        taskData.journalEntryId = data.journalEntryId;
      }

      const response = await apiRequest("PATCH", `/api/tasks/${id}`, taskData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setEditTask(null);
      editForm.reset();
    },
    onError: (error: Error) => {
      toastTaskMutationError("Couldn't update task", error, "Your change was not saved.");
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/tasks/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setDeleteTaskId(null);
    },
    onError: (error: Error) => {
      toastTaskMutationError("Couldn't delete task", error, "The task was not deleted.");
    },
  });

  const handleSubmit = (data: TaskFormData) => {
    createTaskMutation.mutate(data);
  };

  const handleEditSubmit = (data: TaskFormData) => {
    if (editTask) {
      updateTaskMutation.mutate({ id: editTask.id, data });
    }
  };

  const handleDelete = () => {
    if (deleteTaskId) {
      deleteTaskMutation.mutate(deleteTaskId);
    }
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const toggleTaskExpanded = (taskId: string) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const handleAddSubTask = (taskId: string) => {
    if (createSubTaskMutation.isPending) return;
    const text = newSubTaskTexts[taskId]?.trim();
    if (text) {
      createSubTaskMutation.mutate({ taskId, text });
    }
  };

  const handleAddDialogSubTask = () => {
    if (createSubTaskMutation.isPending) return;
    if (!selectedTask) return;
    const text = dialogSubTaskText.trim();
    if (text) {
      createSubTaskMutation.mutate({ taskId: selectedTask.id, text });
      setDialogSubTaskText("");
    }
  };

  const handleCardClick = (task: Task, e: React.MouseEvent) => {
    // Don't open dialog if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('input') ||
      target.closest('[role="checkbox"]')
    ) {
      return;
    }
    setSelectedTask(task);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-500/10 text-red-700 dark:text-red-300 border-red-300";
      case "high":
        return "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-300";
      case "medium":
        return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border-yellow-300";
      case "low":
        return "bg-green-500/10 text-green-700 dark:text-green-300 border-green-300";
      default:
        return "bg-gray-500/10 text-gray-700 dark:text-gray-300 border-gray-300";
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold">Tasks</h2>
            <p className="text-sm text-muted-foreground hidden sm:block">Tasks linked to this goal</p>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)} data-testid="button-create-task" className="shrink-0 min-h-[44px]">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New Task</span>
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-muted-foreground">Loading tasks...</p>
          </div>
        ) : tasks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">No tasks linked to this goal yet</p>
              <Button 
                variant="outline" 
                onClick={() => setIsCreateModalOpen(true)}
                data-testid="button-create-first-task"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Task
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {tasks.map((task) => {
              const progress = getSubTaskProgress(task.id);
              const taskSubTasks = subTasksByTaskId.get(task.id) || [];
              const isExpanded = expandedTasks.has(task.id) || taskSubTasks.length > 0;

              return (
                <Card 
                  key={task.id} 
                  data-testid={`card-task-${task.id}`}
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={(e) => handleCardClick(task, e)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={task.completed}
                        onCheckedChange={(checked) => {
                          toggleTaskMutation.mutate({ id: task.id, completed: checked as boolean });
                        }}
                        className="mt-1"
                        data-testid={`checkbox-task-${task.id}`}
                      />
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium break-words ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </div>
                            {task.description && (
                              <div className="text-sm text-muted-foreground mt-1 break-words">
                                {task.description}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                            {task.completed && (
                              <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-300 hidden sm:flex">
                                Completed
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="min-h-[44px] min-w-[44px] shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditTask(task);
                              }}
                              data-testid={`button-edit-task-${task.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="min-h-[44px] min-w-[44px] text-destructive hover:text-destructive shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTaskId(task.id);
                              }}
                              data-testid={`button-delete-task-${task.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap text-sm">
                          {task.category && (
                            <Badge variant="secondary" className="text-xs" data-testid={`badge-category-${task.id}`}>
                              {task.category}
                            </Badge>
                          )}
                          {task.dueDate && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                              <Calendar className="w-3 h-3 shrink-0" />
                              <span>Due: {formatDate(task.dueDate)}</span>
                            </div>
                          )}
                          {progress && (
                            <Badge variant="outline" className="text-xs whitespace-nowrap" data-testid={`badge-subtask-count-${task.id}`}>
                              <ListTodo className="w-3 h-3 mr-1 shrink-0" />
                              {progress.completed}/{progress.total} sub-tasks
                            </Badge>
                          )}
                        </div>

                        {progress && (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>Progress</span>
                              <span data-testid={`text-progress-${task.id}`}>{progress.percentage}%</span>
                            </div>
                            <Progress value={progress.percentage} className="h-2" data-testid={`progress-bar-${task.id}`} />
                          </div>
                        )}

                        {taskSubTasks.length > 0 && (
                          <Collapsible open={isExpanded}>
                            <CollapsibleTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start px-0 h-auto py-1"
                                onClick={() => toggleTaskExpanded(task.id)}
                                data-testid={`button-toggle-subtasks-${task.id}`}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 mr-1" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 mr-1" />
                                )}
                                <span className="text-sm">
                                  {isExpanded ? "Hide" : "Show"} sub-tasks
                                </span>
                              </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-2 space-y-2">
                              {taskSubTasks.map((subTask) => (
                                <div
                                  key={subTask.id}
                                  className="flex items-start gap-2 pl-4 border-l-2 border-muted"
                                  data-testid={`subtask-item-${subTask.id}`}
                                >
                                  <Checkbox
                                    checked={subTask.completed}
                                    onCheckedChange={(checked) => {
                                      toggleSubTaskMutation.mutate({
                                        id: subTask.id,
                                        completed: checked as boolean,
                                      });
                                    }}
                                    className="mt-0.5"
                                    data-testid={`checkbox-subtask-${subTask.id}`}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div
                                      className={`text-sm break-words ${
                                        subTask.completed ? 'line-through text-muted-foreground' : ''
                                      }`}
                                      data-testid={`text-subtask-${subTask.id}`}
                                    >
                                      {subTask.text}
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-destructive"
                                    onClick={() => deleteSubTaskMutation.mutate(subTask.id)}
                                    data-testid={`button-delete-subtask-${subTask.id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              ))}
                            </CollapsibleContent>
                          </Collapsible>
                        )}

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2">
                          <Input
                            placeholder="Add a sub-task..."
                            value={newSubTaskTexts[task.id] || ""}
                            onChange={(e) => {
                              setNewSubTaskTexts(prev => ({
                                ...prev,
                                [task.id]: e.target.value,
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddSubTask(task.id);
                              }
                            }}
                            className="flex-1 min-h-[44px]"
                            data-testid={`input-new-subtask-${task.id}`}
                          />
                          <Button
                            onClick={() => handleAddSubTask(task.id)}
                            disabled={!newSubTaskTexts[task.id]?.trim() || createSubTaskMutation.isPending}
                            data-testid={`button-add-subtask-${task.id}`}
                            className="w-full sm:w-auto min-h-[44px]"
                          >
                            <Plus className="w-4 h-4 sm:mr-1" />
                            <span className="sm:inline">Add</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Full-Page Task Detail Dialog */}
      <Dialog open={!!selectedTask} onOpenChange={(open) => !open && setSelectedTask(null)}>
        <DialogContent className="h-screen w-screen m-0 p-0 overflow-hidden rounded-none" data-testid="dialog-task-detail">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-start justify-between p-6 border-b">
              <div className="flex-1 pr-8">
                <h2 className="text-3xl font-bold mb-2" data-testid="dialog-task-title">
                  {selectedTask?.title}
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" data-testid="dialog-task-category">
                    {selectedTask?.category || "Uncategorized"}
                  </Badge>
                  <Badge 
                    variant="outline" 
                    className={getPriorityColor(selectedTask?.priority || "medium")}
                    data-testid="dialog-task-priority"
                  >
                    {selectedTask?.priority || "medium"} priority
                  </Badge>
                  {selectedTask?.completed && (
                    <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-300" data-testid="dialog-task-completed">
                      Completed
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedTask(null)}
                data-testid="button-close-task-dialog"
                className="shrink-0 min-h-[44px] min-w-[44px]"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-4xl mx-auto space-y-6">
                {/* Description */}
                {selectedTask?.description && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Description</h3>
                    <p className="text-muted-foreground" data-testid="dialog-task-description">
                      {selectedTask.description}
                    </p>
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedTask?.createdAt && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Created</p>
                        <p className="text-sm text-muted-foreground" data-testid="dialog-task-created">
                          {formatDate(selectedTask.createdAt)}
                        </p>
                      </div>
                    </div>
                  )}
                  {selectedTask?.dueDate && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Due Date</p>
                        <p className="text-sm text-muted-foreground" data-testid="dialog-task-due-date">
                          {formatDate(selectedTask.dueDate)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sub-tasks */}
                {selectedTask && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Sub-tasks</h3>
                      {(() => {
                        const progress = getSubTaskProgress(selectedTask.id);
                        if (progress) {
                          return (
                            <span className="text-sm text-muted-foreground" data-testid="dialog-subtask-progress">
                              {progress.completed}/{progress.total} completed ({progress.percentage}%)
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>

                    {/* Progress Bar */}
                    {(() => {
                      const progress = getSubTaskProgress(selectedTask.id);
                      if (progress) {
                        return (
                          <div className="mb-4">
                            <Progress value={progress.percentage} className="h-2" data-testid="dialog-progress-bar" />
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Sub-task List */}
                    <div className="space-y-2 mb-4">
                      {(() => {
                        const taskSubTasks = subTasksByTaskId.get(selectedTask.id) || [];
                        if (taskSubTasks.length === 0) {
                          return (
                            <p className="text-sm text-muted-foreground py-4 text-center" data-testid="dialog-no-subtasks">
                              No sub-tasks yet. Add one below!
                            </p>
                          );
                        }
                        return taskSubTasks.map((subTask) => (
                          <div
                            key={subTask.id}
                            className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                            data-testid={`dialog-subtask-item-${subTask.id}`}
                          >
                            <Checkbox
                              checked={subTask.completed}
                              onCheckedChange={(checked) => {
                                toggleSubTaskMutation.mutate({
                                  id: subTask.id,
                                  completed: checked as boolean,
                                });
                              }}
                              className="mt-0.5"
                              data-testid={`dialog-checkbox-subtask-${subTask.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div
                                className={`break-words ${
                                  subTask.completed ? 'line-through text-muted-foreground' : ''
                                }`}
                                data-testid={`dialog-text-subtask-${subTask.id}`}
                              >
                                {subTask.text}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="min-h-[44px] min-w-[44px] text-destructive shrink-0"
                              onClick={() => deleteSubTaskMutation.mutate(subTask.id)}
                              data-testid={`dialog-button-delete-subtask-${subTask.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ));
                      })()}
                    </div>

                    {/* Add Sub-task */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <Input
                        placeholder="Add a new sub-task..."
                        value={dialogSubTaskText}
                        onChange={(e) => setDialogSubTaskText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddDialogSubTask();
                          }
                        }}
                        className="flex-1 min-h-[44px]"
                        data-testid="dialog-input-new-subtask"
                      />
                      <Button
                        onClick={handleAddDialogSubTask}
                        disabled={!dialogSubTaskText.trim() || createSubTaskMutation.isPending}
                        data-testid="dialog-button-add-subtask"
                        className="w-full sm:w-auto min-h-[44px]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Sub-task
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent data-testid="dialog-create-task" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter task title" {...field} data-testid="input-task-title" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter task description" 
                        {...field} 
                        data-testid="input-task-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || "Uncategorized"}>
                      <FormControl>
                        <SelectTrigger data-testid="select-task-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                        <CategorySelectGroups categories={categories} />
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || "medium"}>
                      <FormControl>
                        <SelectTrigger data-testid="select-task-priority">
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        type="date" 
                        {...field} 
                        data-testid="input-task-due-date"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="showOnMainTaskPage"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                    <div className="space-y-0.5">
                      <FormLabel>Show on Main Task Page</FormLabel>
                      <div className="text-sm text-muted-foreground">
                        When disabled, this task will only appear within this goal
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="switch-show-on-main-task-page"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsCreateModalOpen(false)}
                  data-testid="button-cancel-create-task"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={createTaskMutation.isPending}
                  data-testid="button-submit-create-task"
                >
                  {createTaskMutation.isPending ? "Creating..." : "Create Task"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={!!editTask} onOpenChange={(open) => !open && setEditTask(null)}>
        <DialogContent data-testid="dialog-edit-task" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit(handleEditSubmit)} className="space-y-4">
              <FormField
                control={editForm.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter task title" {...field} data-testid="input-edit-task-title" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter task description" 
                        {...field} 
                        data-testid="input-edit-task-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || "Uncategorized"}>
                      <FormControl>
                        <SelectTrigger data-testid="select-edit-task-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                        <CategorySelectGroups categories={categories} />
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || "medium"}>
                      <FormControl>
                        <SelectTrigger data-testid="select-edit-task-priority">
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        type="date" 
                        {...field} 
                        data-testid="input-edit-task-due-date"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-3">
                <FormLabel>Link Type</FormLabel>
                <RadioGroup 
                  value={editLinkType} 
                  onValueChange={(v) => setEditLinkType(v as typeof editLinkType)} 
                  data-testid="radio-edit-link-type"
                >
                  <div className="flex items-center space-x-2 py-1">
                    <RadioGroupItem value="none" id="edit-none" data-testid="radio-edit-link-none" />
                    <label htmlFor="edit-none" className="cursor-pointer text-sm flex-1">None (Standalone Task)</label>
                  </div>
                  <div className="flex items-center space-x-2 py-1">
                    <RadioGroupItem value="goal" id="edit-goal" data-testid="radio-edit-link-goal" />
                    <label htmlFor="edit-goal" className="cursor-pointer text-sm flex-1">Link to Goal</label>
                  </div>
                  <div className="flex items-center space-x-2 py-1">
                    <RadioGroupItem value="milestone" id="edit-milestone" data-testid="radio-edit-link-milestone" />
                    <label htmlFor="edit-milestone" className="cursor-pointer text-sm flex-1">Link to Milestone</label>
                  </div>
                  <div className="flex items-center space-x-2 py-1">
                    <RadioGroupItem value="journal" id="edit-journal" data-testid="radio-edit-link-journal" />
                    <label htmlFor="edit-journal" className="cursor-pointer text-sm flex-1">Link to Journal Entry</label>
                  </div>
                </RadioGroup>
              </div>

              {editLinkType === "goal" && (
                <FormField
                  control={editForm.control}
                  name="goalId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Select Goal</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || undefined}>
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-goal">
                            <SelectValue placeholder="Select a goal" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {allGoals.map((goal) => (
                            <SelectItem key={goal.id} value={goal.id}>
                              {goal.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {editLinkType === "milestone" && (
                <>
                  <div>
                    <FormLabel>Select Goal First</FormLabel>
                    <Select onValueChange={setEditSelectedGoalId} value={editSelectedGoalId}>
                      <SelectTrigger data-testid="select-edit-goal-for-milestone">
                        <SelectValue placeholder="Select a goal" />
                      </SelectTrigger>
                      <SelectContent>
                        {allGoals.map((goal) => (
                          <SelectItem key={goal.id} value={goal.id}>
                            {goal.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {editSelectedGoalId && (
                    <FormField
                      control={editForm.control}
                      name="milestoneId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Select Milestone</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || undefined}>
                            <FormControl>
                              <SelectTrigger data-testid="select-edit-milestone">
                                <SelectValue placeholder="Select a milestone" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {editMilestones.map((milestone) => (
                                <SelectItem key={milestone.id} value={milestone.id}>
                                  {milestone.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </>
              )}

              {editLinkType === "journal" && (
                <FormField
                  control={editForm.control}
                  name="journalEntryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Select Journal Entry</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || undefined}>
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-journal-entry">
                            <SelectValue placeholder="Select a journal entry" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {journalEntries.map((entry) => (
                            <SelectItem key={entry.id} value={entry.id}>
                              {entry.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setEditTask(null)}
                  data-testid="button-cancel-edit-task"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={updateTaskMutation.isPending}
                  data-testid="button-submit-edit-task"
                >
                  {updateTaskMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Task Confirmation */}
      <AlertDialog open={!!deleteTaskId} onOpenChange={(open) => !open && setDeleteTaskId(null)}>
        <AlertDialogContent data-testid="alert-delete-task" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this task? This action cannot be undone and will also delete all sub-tasks associated with this task.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-task">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-task"
            >
              {deleteTaskMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
